import { objectFromEntries, objectEntries } from "../../utils.mjs";
import { getStatesByCountry } from "../states.mjs";
import { hasAnyMilitaryPostal } from "./shipping/militaryPostalValidation.mjs";
import { isPOBox } from "./shipping/poBoxValidation.mjs";
const email = (email) => {
    const valid = email != undefined && email.includes('@') && email.length > 2;
    if (!valid) {
        return [{ code: 'email_invalid' }];
    }
    return [];
};
const phoneNumber = (phoneNumber) => {
    const valid = phoneNumber != undefined && !!phoneNumber.match(/^\+?[\d-\s]{5,30}$/);
    if (!valid) {
        return [{ code: 'phone_number_invalid' }];
    }
    return [];
};
const companyName = (companyName, isCompany) => {
    if ((companyName === undefined || companyName.length < 2) && isCompany) {
        return [{ code: 'company_name_invalid' }];
    }
    return [];
};
const vatNumber = (isCompany, showVatInput, status) => {
    if (!isCompany || !showVatInput) {
        return [];
    }
    switch (status) {
        case 'invalid':
            return [{ code: 'vat_number_invalid' }];
        case undefined:
        case 'not validated':
            return [{ code: 'vat_number_not_validated' }];
        case 'validated':
            return [];
    }
};
export const addressValidation = (args) => {
    var _a;
    const { countryCode, address, addressType, skipFields = [] } = args;
    let result = [];
    const addressTypeToCode = (code, addressType) => ({
        code: ((addressType === 'address' ? 'shipping_' : 'billing_') + code),
    });
    // Check if the address is a PO Box, which is not valid for shipping
    if (addressType === 'address' && (isPOBox(address.address1) || isPOBox((_a = address.address2) !== null && _a !== void 0 ? _a : ''))) {
        result = [...result, addressTypeToCode('po_box_not_valid_for_shipping', addressType)];
    }
    // Check if the address is a military postal address (APO/FPO/DPO), which is not valid for shipping
    if (addressType === 'address' && hasAnyMilitaryPostal(address.address1, address.address2, address.city, address.stateOrProvince)) {
        result = [...result, addressTypeToCode('military_postal_not_valid_for_shipping', addressType)];
    }
    if (addressType === 'address') {
        result = [...result, ...addressFieldsValidation({ countryCode, address, addressType, skipFields })];
    }
    if (addressType === 'billingAddress') {
        result = [...result, ...addressFieldsValidation({ countryCode, address, addressType, skipFields })];
    }
    return result;
};
const addressTypeToCode = (code, addressType) => ({
    code: ((addressType === 'address' ? 'shipping_' : 'billing_') + code),
});
export const addressFieldsValidation = (args) => {
    const { countryCode, address, addressType, skipFields = [] } = args;
    const trimmedAddress = trimAddress(address, skipFields);
    const addressCountryCode = addressType === 'billingAddress' ? address.countryCode : countryCode;
    const fields = [
        {
            name: 'firstName',
            result: 'firstname_invalid',
            validator: (value) => { var _a; return ((_a = value === null || value === void 0 ? void 0 : value.length) !== null && _a !== void 0 ? _a : 0) > 1; },
        },
        {
            name: 'lastName',
            result: 'lastname_invalid',
            validator: (value) => { var _a; return ((_a = value === null || value === void 0 ? void 0 : value.length) !== null && _a !== void 0 ? _a : 0) > 1; },
        },
        {
            name: 'address1',
            result: 'address1_invalid',
            validator: (value) => { var _a; return ((_a = value === null || value === void 0 ? void 0 : value.length) !== null && _a !== void 0 ? _a : 0) > 2; },
        },
        {
            name: 'city',
            result: 'city_invalid',
            validator: (value) => { var _a; return ((_a = value === null || value === void 0 ? void 0 : value.length) !== null && _a !== void 0 ? _a : 0) > 1; },
        },
        {
            name: 'postalCode',
            result: 'postal_code_invalid',
            validator: (value) => { var _a; return ((_a = value === null || value === void 0 ? void 0 : value.length) !== null && _a !== void 0 ? _a : 0) > 2; },
        },
        {
            name: 'stateOrProvince',
            result: 'state_or_province_invalid',
            validator: (value) => {
                var _a;
                if (!addressCountryCode) {
                    return false;
                }
                return !getStatesByCountry(addressCountryCode) || ((_a = value === null || value === void 0 ? void 0 : value.length) !== null && _a !== void 0 ? _a : 0) >= 2;
            },
        },
        {
            name: 'countryCode',
            result: 'country_code_invalid',
            validator: (value) => { var _a; return (addressType === 'billingAddress' ? ((_a = value === null || value === void 0 ? void 0 : value.length) !== null && _a !== void 0 ? _a : 0) > 1 : true); },
        },
    ];
    return fields
        .filter((field) => !(skipFields === null || skipFields === void 0 ? void 0 : skipFields.includes(field.name)))
        .map((field) => field.validator(trimmedAddress[field.name]) ? undefined : addressTypeToCode(field.result, addressType))
        .filter((result) => result !== undefined);
};
const billingAddress = (args, skipFields = []) => {
    const { customer, countryCode } = args;
    if (!(customer === null || customer === void 0 ? void 0 : customer.useBillingAddress)) {
        return [];
    }
    if (!customer.billingAddress) {
        return [{ code: 'billing_address_missing' }];
    }
    return addressValidation({ countryCode, address: customer.billingAddress, addressType: 'billingAddress', skipFields });
};
const shippingAddress = (args, skipFields = []) => {
    const { customer, countryCode } = args;
    if (!(customer === null || customer === void 0 ? void 0 : customer.address)) {
        return [{ code: 'shipping_address_missing' }];
    }
    return addressValidation({ countryCode, address: customer.address, addressType: 'address', skipFields });
};
/**
 * Specific conditions where we skip certain address fields during validation
 */
const skipAddressValidationFields = (psp, countryCode) => {
    return psp === 'stripe' && countryCode === 'jp' ? ['city'] : undefined;
};
const hasShippingMethod = (shipping) => {
    const hasMethod = !((shipping === null || shipping === void 0 ? void 0 : shipping.$type) === 'checkoutPage' && !shipping.selectedShippingMethod);
    if (!hasMethod) {
        return [{ code: 'shipping_method_missing' }];
    }
    return [];
};
const paymentStatusCodes = (payment) => {
    if (!payment) {
        return 'missingPaymentMethod';
    }
    if (payment.$type === 'checkoutPage') {
        return payment.selectedPaymentMethod ? 'ready' : 'missingPaymentMethod';
    }
    if (payment.$type === 'free' || payment.$type === 'stripeCheckout') {
        return 'ready';
    }
    if (payment.$type === 'alreadyPaid') {
        return 'alreadyPaid';
    }
    return undefined;
};
const paymentStatus = (payment) => {
    const status = paymentStatusCodes(payment);
    switch (status) {
        case 'missingPaymentMethod':
            return 'payment_method_missing';
        case 'alreadyPaid':
            return 'payment_already_paid';
        default:
            break;
    }
    return undefined;
};
const tax = (code) => {
    if (!code) {
        return [];
    }
    return [{ code: translateTaxErrorCode(code) }];
};
/**
 * Translate error codes from the tax providers into cart validation codes
 */
const translateTaxErrorCode = (code) => {
    switch (code) {
        case 'taxjar_taxfororder_failed':
            return 'tax_calculation_failed';
        case 'taxjar_no_shipping_address_found':
            return 'tax_no_address_found';
        case 'taxjar_missing_required_address_data':
            return 'tax_missing_address_data';
        case 'taxjar_invalid_postal_code':
            // for the tax specific code we return "tax_invalid_postal_code"
            // we set it like this for now to reduce duplication of similar responses
            return 'shipping_postal_code_invalid';
        case 'stripe_param_validation_error':
            return 'tax_param_validation_error';
        case 'stripe_unknown_error':
            return 'tax_unknown_error';
        case 'internal_error':
            return 'tax_internal_error';
        default:
            return 'tax_unknown_error';
    }
};
export const trimAddress = (address, skipKeys) => {
    return objectFromEntries(objectEntries(address)
        .filter(([_key, value]) => value !== false) // filter out any false values
        .map(([key, value]) => { var _a; return [key, typeof value === 'string' && !(skipKeys === null || skipKeys === void 0 ? void 0 : skipKeys.includes(key)) ? (_a = value === null || value === void 0 ? void 0 : value.trim()) !== null && _a !== void 0 ? _a : '' : value]; }));
};
export const sdkValidations = {
    email,
    phoneNumber,
    companyName,
    vatNumber,
    shippingAddress,
    billingAddress,
    hasShippingMethod,
    paymentStatus,
    skipAddressValidationFields,
    tax,
};
//# sourceMappingURL=shared.js.map